/**
 * ChatGPT-Ads-Pixel (Browserseite). Der Loader in
 * `components/analytics/openai-ads-pixel.tsx` definiert `window.oaiq` als
 * Queue-Shim auf JEDEM Host und lädt das SDK nur auf *.isg-express.com —
 * so bleiben Aufrufe lokal prüfbar (Queue), ohne Testdaten zu senden.
 *
 * Datenvertrag (Übergabe 11.09.2026): `order_created` nur für serverseitig
 * bezahlte Buchungen, Betrag in Cent, `event_id` = Auftragsnummer (#ISG-…)
 * identisch zur Conversions API (Dedup über Pixel-ID + Event + id).
 */
declare global {
  interface Window {
    oaiq?: ((...args: unknown[]) => void) & { q?: unknown[][] };
    /** Lädt das SDK nach (nur *.isg-express.com), definiert im Pixel-Loader. */
    __oaiqLoad?: () => void;
    __oaiqGranted?: boolean;
  }
}

export const OPENAI_ADS_HOST_RE = /(^|\.)isg-express\.com$/;

/** Name des vanilla-cookieconsent-Cookies (Parent-Domain .isg-express.com). */
const CC_COOKIE = "cc_cookie";

/** Marketing-Einwilligung aus dem bestehenden Cookie-Banner (kein zweiter Consent). */
export function marketingConsentGranted(): boolean {
  if (typeof document === "undefined") return false;
  const m = document.cookie.match(new RegExp(`(?:^|; )${CC_COOKIE}=([^;]*)`));
  if (!m) return false;
  try {
    const parsed = JSON.parse(decodeURIComponent(m[1])) as { categories?: unknown };
    return Array.isArray(parsed.categories) && parsed.categories.includes("marketing");
  } catch {
    return false;
  }
}

/** Roher `__oppref`-Wert (vom SDK gesetzt), undurchsichtig weiterreichen. */
export function readOppref(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|; )__oppref=([^;]*)/);
  if (!m) return null;
  const v = decodeURIComponent(m[1]).trim();
  return v && v.length <= 512 ? v : null;
}

export function oaiqConsent(granted: boolean): void {
  if (typeof window === "undefined") return;
  window.oaiq?.("consent", granted);
  // Ohne Einwilligung bleibt das SDK ungeladen (kein Ping); mit Einwilligung
  // nachladen — die Queue (init, measure) wird dann vom SDK abgearbeitet.
  if (granted) {
    window.__oaiqGranted = true;
    window.__oaiqLoad?.();
  }
}

export type OrderCreatedBrowserEvent = {
  /** Stabile Dedup-ID = Auftragsnummer `#ISG-…`. */
  eventId: string;
  amountCents: number;
  currency: string;
};

/** Browser-Pfad des Kaufs. Nur aufrufen, wenn der Kauf serverseitig
 *  verifiziert ist (Stripe `succeeded` im Flow bzw. `verifyReturnedIntent`). */
export function measureOrderCreated(e: OrderCreatedBrowserEvent): void {
  if (typeof window === "undefined") return;
  if (!Number.isInteger(e.amountCents) || e.amountCents <= 0) return;
  // Ohne Marketing-Einwilligung nicht einmal in die Queue legen (18.09.2026):
  // das SDK bliebe zwar ungeladen, aber der Nachweis soll eindeutig sein.
  if (!marketingConsentGranted()) return;
  window.oaiq?.(
    "measure",
    "order_created",
    { type: "contents", amount: e.amountCents, currency: e.currency.toUpperCase() },
    { event_id: e.eventId },
  );
}
